import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'react-hot-toast';
import {
  CheckIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

import { useAuth } from '../contexts/AuthContext';
import { usersApi, emptyPreferences } from '../services/api/users';
import type { ExperienceLevel, UserPreferences } from '../types/user';
import TagInput from '../components/onboarding/TagInput';
import { billingApi, formatPrice, UNLIMITED, type Plan } from '../services/api/billing';
import LoadingSpinner from '../components/common/LoadingSpinner';
import {
  ROLE_SUGGESTIONS,
  SKILL_SUGGESTIONS,
  LOCATION_SUGGESTIONS,
  EXPERIENCE_OPTIONS,
  CURRENCIES,
  STEPS,
} from '../components/onboarding/steps';

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(0);
  const [prefs, setPrefs] = useState<UserPreferences>(emptyPreferences);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [paymentsEnabled, setPaymentsEnabled] = useState(true);

  // Plans are fetched up front so the final step renders instantly
  useEffect(() => {
    billingApi
      .getPlans()
      .then((data) => {
        setPlans(data.plans);
        setPaymentsEnabled(data.payments_enabled);
      })
      .catch(() => undefined);
  }, []);

  // Resume where the user left off rather than restarting the wizard
  useEffect(() => {
    usersApi
      .getPreferences()
      .then((data) => setPrefs({ ...emptyPreferences, ...data }))
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, []);

  const set = <K extends keyof UserPreferences>(key: K, value: UserPreferences[K]) =>
    setPrefs((p) => ({ ...p, [key]: value }));

  const canAdvance = step !== 0 || prefs.desired_roles.length > 0;
  const isLast = step === STEPS.length - 1;

  // Persist on every step so a dropped session is never lost work
  const persist = async (completed = false) => {
    try {
      await usersApi.updatePreferences({ ...prefs, onboarding_completed: completed });
    } catch {
      toast.error('Could not save your answers. Check your connection.');
      throw new Error('save failed');
    }
  };

  const next = async () => {
    if (!canAdvance) return;
    setSaving(true);
    try {
      await persist(false);
      setStep((s) => Math.min(s + 1, STEPS.length - 1));
    } catch {
      /* toast already shown */
    } finally {
      setSaving(false);
    }
  };

  const finish = async () => {
    setSaving(true);
    try {
      await persist(true);
      await refreshUser();
      toast.success('Your feed is ready');
      navigate('/jobs', { replace: true });
    } catch {
      /* toast already shown */
    } finally {
      setSaving(false);
    }
  };

  const choosePlan = async (plan: Plan) => {
    setSaving(true);
    try {
      // Mark onboarding done before leaving, so returning from Paystack
      // does not bounce the user back into the wizard.
      await persist(true);
      await refreshUser();
      if (plan.amount === 0) {
        navigate('/jobs', { replace: true });
        return;
      }
      const { authorization_url } = await billingApi.startCheckout(plan.code);
      window.location.href = authorization_url;
    } catch (error: any) {
      if (error?.message !== 'save failed') {
        toast.error('Could not open checkout. You can upgrade any time from Plans.');
      }
      setSaving(false);
    }
  };

  const skip = async () => {
    setSaving(true);
    try {
      await persist(true);
      await refreshUser();
      navigate('/jobs', { replace: true });
    } catch {
      /* toast already shown */
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  const current = STEPS[step];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white flex flex-col">
      {/* Progress */}
      <div className="w-full max-w-2xl mx-auto px-6 pt-10">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-blue-600">
            <SparklesIcon className="w-5 h-5" />
            <span className="text-sm font-semibold">
              Step {step + 1} of {STEPS.length}
            </span>
          </div>
          <button
            onClick={skip}
            disabled={saving}
            className="text-sm text-gray-500 hover:text-gray-800 transition-colors disabled:opacity-50"
          >
            Skip for now
          </button>
        </div>
        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
          <motion.div
            className="h-full bg-blue-600"
            initial={false}
            animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
            transition={{ duration: 0.35, ease: 'easeOut' }}
          />
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 w-full max-w-2xl mx-auto px-6 py-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={current.id}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -24 }}
            transition={{ duration: 0.25 }}
          >
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
              {step === 0 && user?.name ? `Welcome, ${user.name.split(' ')[0]}. ` : ''}
              {current.title}
            </h1>
            <p className="text-gray-600 mb-8">{current.subtitle}</p>

            {current.id === 'roles' && (
              <TagInput
                ariaLabel="Desired roles"
                value={prefs.desired_roles}
                onChange={(v) => set('desired_roles', v)}
                placeholder="e.g. Senior Software Developer"
                suggestions={ROLE_SUGGESTIONS}
                max={10}
              />
            )}

            {current.id === 'skills' && (
              <TagInput
                ariaLabel="Skills"
                value={prefs.skills}
                onChange={(v) => set('skills', v)}
                placeholder="e.g. Python"
                suggestions={SKILL_SUGGESTIONS}
                max={30}
              />
            )}

            {current.id === 'location' && (
              <div className="space-y-6">
                <TagInput
                  ariaLabel="Preferred locations"
                  value={prefs.preferred_locations}
                  onChange={(v) => set('preferred_locations', v)}
                  placeholder="e.g. Cape Town"
                  suggestions={LOCATION_SUGGESTIONS}
                  max={10}
                />
                <label className="flex items-start gap-3 p-4 border border-gray-200 rounded-xl cursor-pointer hover:border-blue-400 transition-colors">
                  <input
                    type="checkbox"
                    checked={prefs.open_to_remote}
                    onChange={(e) => set('open_to_remote', e.target.checked)}
                    className="mt-1 w-5 h-5 rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span>
                    <span className="block font-medium text-gray-900">
                      I am open to remote work
                    </span>
                    <span className="block text-sm text-gray-500">
                      Remote roles will be boosted in your feed.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {current.id === 'experience' && (
              <div className="space-y-6">
                <div className="grid gap-3">
                  {EXPERIENCE_OPTIONS.map((opt) => {
                    const selected = prefs.experience_level === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => set('experience_level', opt.value as ExperienceLevel)}
                        className={`flex items-center justify-between text-left p-4 rounded-xl border transition-all ${
                          selected
                            ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-600'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span>
                          <span className="block font-medium text-gray-900">{opt.label}</span>
                          <span className="block text-sm text-gray-500">{opt.description}</span>
                        </span>
                        {selected && <CheckIcon className="w-5 h-5 text-blue-600 shrink-0" />}
                      </button>
                    );
                  })}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Minimum salary <span className="text-gray-400">(optional)</span>
                  </label>
                  <div className="flex gap-3">
                    <select
                      value={prefs.salary_currency ?? 'ZAR'}
                      onChange={(e) => set('salary_currency', e.target.value)}
                      className="px-3 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                      aria-label="Salary currency"
                    >
                      {CURRENCIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min={0}
                      value={prefs.min_salary ?? ''}
                      onChange={(e) =>
                        set('min_salary', e.target.value ? Number(e.target.value) : null)
                      }
                      placeholder="e.g. 600000"
                      aria-label="Minimum salary"
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none"
                    />
                  </div>
                </div>
              </div>
            )}

            {current.id === 'plan' && (
              <div className="space-y-3">
                {!paymentsEnabled && (
                  <div className="px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-sm">
                    Checkout is not available yet — start on Free and upgrade later.
                  </div>
                )}
                {plans.map((plan) => {
                  const disabled = saving || (plan.amount > 0 && !paymentsEnabled);
                  return (
                    <button
                      key={plan.code}
                      type="button"
                      disabled={disabled}
                      onClick={() => choosePlan(plan)}
                      className={`w-full text-left p-5 rounded-2xl border transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                        plan.highlight
                          ? 'border-blue-600 bg-blue-50/50 hover:bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-baseline justify-between gap-4 mb-1">
                        <span className="font-semibold text-gray-900">
                          {plan.name}
                          {plan.highlight && (
                            <span className="ml-2 text-[10px] font-bold tracking-wide text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full align-middle">
                              MOST POPULAR
                            </span>
                          )}
                        </span>
                        <span className="font-bold text-gray-900 whitespace-nowrap">
                          {plan.amount === 0 ? 'Free' : formatPrice(plan)}
                          {plan.days > 0 && (
                            <span className="text-gray-500 font-normal text-sm">
                              {' '}/ {plan.days} days
                            </span>
                          )}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{plan.tagline}</p>
                      <p className="text-xs text-gray-500">
                        {plan.max_tracked_applications === UNLIMITED
                          ? 'Unlimited tracked applications'
                          : `${plan.max_tracked_applications} tracked applications`}
                        {' · '}
                        {plan.max_cv_analyses === UNLIMITED
                          ? 'Unlimited CV analyses'
                          : `${plan.max_cv_analyses} CV ${plan.max_cv_analyses === 1 ? 'analysis' : 'analyses'}`}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}

            {current.id === 'review' && (
              <div className="space-y-4">
                {[
                  { label: 'Roles', value: prefs.desired_roles.join(', ') },
                  { label: 'Skills', value: prefs.skills.join(', ') },
                  { label: 'Locations', value: prefs.preferred_locations.join(', ') },
                  { label: 'Remote', value: prefs.open_to_remote ? 'Yes' : 'No' },
                  {
                    label: 'Experience',
                    value:
                      EXPERIENCE_OPTIONS.find((o) => o.value === prefs.experience_level)?.label ??
                      '',
                  },
                  {
                    label: 'Minimum salary',
                    value: prefs.min_salary
                      ? `${prefs.salary_currency ?? ''} ${prefs.min_salary.toLocaleString()}`
                      : '',
                  },
                ].map((row) => (
                  <div
                    key={row.label}
                    className="flex justify-between gap-6 py-3 border-b border-gray-100"
                  >
                    <span className="text-sm font-medium text-gray-500 shrink-0">{row.label}</span>
                    <span className="text-sm text-gray-900 text-right">
                      {row.value || <span className="text-gray-400">Not set</span>}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="sticky bottom-0 bg-white/90 backdrop-blur border-t border-gray-200">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => setStep((s) => Math.max(s - 1, 0))}
            disabled={step === 0 || saving}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:text-gray-900 disabled:opacity-0 transition"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Back
          </button>

          {current.id === 'plan' ? (
            <span className="text-sm text-gray-500">Choose a plan above to finish</span>
          ) : (
          <button
            onClick={isLast ? finish : next}
            disabled={!canAdvance || saving}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {saving ? 'Saving...' : 'Continue'}
            {!saving && <ArrowRightIcon className="w-4 h-4" />}
          </button>
          )}
        </div>
        {step === 0 && !canAdvance && (
          <p className="max-w-2xl mx-auto px-6 pb-3 -mt-2 text-xs text-gray-500">
            Add at least one role to continue.
          </p>
        )}
      </div>
    </div>
  );
};

export default OnboardingPage;
